import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ordersKeyFactory } from "@features/orders/queries";
import { OrderSet } from "@models/OrderSet";
import client from "@services/api";
import mapPaginatedRows from "@utils/reactQuery/mapPaginatedRows";
import { Paginated } from "@utils/reactQuery/types";

import { orderSetsKeyFactory } from "../../../queries/orderSetQueries";
import useOrderSetId from "../useOrderSetId";

export default function useSubmitOrderSetMutation() {
  const queryClient = useQueryClient();
  const orderSetId = useOrderSetId();

  return useMutation({
    mutationFn: () => {
      return client.post<OrderSet>(
        `order-sets/${orderSetId}/submit`,
        undefined,
        { axiosOptions: { timeout: 10 * 60_1000 } }
      );
    },
    onSuccess: () => {
      queryClient.setQueryData<OrderSet>(
        orderSetsKeyFactory.detail(orderSetId).queryKey,
        (os) => os && { ...os, status: "submitted" }
      );
      // Mark it submitted wherever it is cached, including lists nothing is
      // watching: a catalog mounting later reads its list cache before the
      // refetch lands, and the draft matcher only rejects a submitted status.
      // Lists it belongs to now (history, approvals) keep the row.
      queryClient.setQueriesData<Paginated<OrderSet>>(
        { queryKey: orderSetsKeyFactory.paginated._def },
        mapPaginatedRows((rows: OrderSet[]) =>
          rows.map((os) =>
            os.id === orderSetId ? { ...os, status: "submitted" } : os
          )
        )
      );
      queryClient.invalidateQueries({
        queryKey: orderSetsKeyFactory.paginated._def,
      });
      queryClient.invalidateQueries({
        queryKey: ordersKeyFactory.paginated._def,
      });
    },
  });
}
